import { JWT_KEY } from "../config/constant";
import { HttpClient } from "./http-client";
import { Auth } from "./Auth";
import { Document } from "./Document";
import { Notice } from "./Notice";
import { S3 } from "./S3";
import { User } from "./user";
import { Tag } from "./Tag";
import axios, { AxiosResponse } from "axios";
import { clearJwt, clearUser, isJwtExists, saveJwt } from "utils/utility";
import qs from "qs";
import { Dashboard } from "./Dashboard";
import { ROUTER_PATH } from "config/path";
import { modalStore } from "components/Common/Alert/AlertManager";
import { handleAppLoginSuccess, handleLogout } from "../utils/appInterface";
import { PushNotification } from "./PushNotification";

let refreshRequestPromise: Promise<AxiosResponse> | null = null;

const instance = new HttpClient({
  baseURL: process.env.REACT_APP_API,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: "repeat" });
  },
  withCredentials: true,
});
// instance is axios instance so you can use axios instance method (ex. interceptors)

instance.instance.interceptors.request.use(
  (config) => {
    if (!navigator.onLine) {
      modalStore.register({
        id: "network-error-modal",
        title: "네트워크 연결 오류",
        content: "인터넷 연결이 끊어졌습니다. 연결 상태를 확인해 주세요.",
      });
      return Promise.reject(new Error("ERR_INTERNET_DISCONNECTED"));
    }

    const jwt = window.localStorage.getItem(JWT_KEY);
    config.headers.Authorization = `Bearer ${jwt}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

//
const PATHS_WITH_CUSTOM_MODAL = [
  { method: "post", url: "/app/auths" },
  { method: "post", url: "/app/users" },
];

instance.instance.interceptors.response.use(
  (response) => {
    console.log("network log", response);
    if (response.config.responseType === "blob") {
      return response;
    }

    if (response.data.isSuccess) {
      return response;
    } else {
      return Promise.reject(response.data.message);
    }
  },
  async (error) => {
    if (
      error.code === "ERR_NETWORK" ||
      error.code === "ERR_INTERNET_DISCONNECTED"
    ) {
      modalStore.register({
        id: "network-error-modal",
        title: `네트워크 연결 오류`,
        content: "인터넷 연결이 불안정합니다. 연결 상태를 확인해 주세요.",
      });
      return Promise.reject("네트워크 연결이 불안정합니다.");
    }

    const originalRequest = error.config;
    const skipGlobalErrorModalHeader =
      typeof originalRequest?.headers?.get === "function"
        ? originalRequest.headers.get("X-Skip-Error-Modal")
        : originalRequest?.headers?.["X-Skip-Error-Modal"] ||
          originalRequest?.headers?.["x-skip-error-modal"];
    const shouldSkipGlobalErrorModal = skipGlobalErrorModalHeader === "true";

    const hasCustomModal = !!PATHS_WITH_CUSTOM_MODAL.find(
      ({ method, url }) =>
        originalRequest?.method === method && originalRequest?.url === url,
    );

    if (
      !shouldSkipGlobalErrorModal &&
      !hasCustomModal &&
      error.response?.status >= 500 &&
      !axios.isCancel(error)
    ) {
      modalStore.register({
        id: "error-modal",
        title: "일시적 오류 발생",
        content: "일시적인 오류가 발생했습니다. 잠시 후에 다시 시도해 주세요.",
      });
    } else if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      window.location.pathname !== ROUTER_PATH.auth.signIn
    ) {
      originalRequest._retry = true;

      // Refresh Request의 Promise가 존재하지 않는 경우 (즉, 기존에 Refresh Token 요청이 없었을 경우)
      if (!refreshRequestPromise) {
        // JWT 초기화
        clearJwt();

        // 신규 토큰 요청 처리
        refreshRequestPromise = axios.post(
          `${process.env.REACT_APP_API}/app/auths/renewal`,
          {},
          {
            withCredentials: true,
          },
        );
      }

      try {
        // 요청 처리를 기다리기
        const res = await refreshRequestPromise;

        // 신규 토큰 찾기
        const newAccessToken = res.data.result?.accessToken;

        // JWT가 없는 경우
        if (!isJwtExists()) {
          // JWT 저장
          saveJwt(newAccessToken);
          // Access Token 재발급했으므로 로그인 완료한 것으로 처리
          handleAppLoginSuccess();
        }

        // 신규 토큰으로 재요청
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return instance.instance(originalRequest);
      } catch (refreshError) {
        console.error("token refresh failed", refreshError);

        // Logout 처리
        logout();

        const isAuthPage = window.location.pathname.startsWith(
          ROUTER_PATH.auth._path,
        );

        if (!isAuthPage) {
          window.location.href = ROUTER_PATH.auth.signIn;
        }

        return Promise.reject(error);
      } finally {
        refreshRequestPromise = null;
      }
    }

    return Promise.reject(error);
  },
);

const api = {
  auth: new Auth(instance),
  dashboard: new Dashboard(instance),
  document: new Document(instance),
  notice: new Notice(instance),
  s3: new S3(instance),
  user: new User(instance),
  tag: new Tag(instance),
  pushNotification: new PushNotification(instance),
};

export default api;

export function logout() {
  // App에 로그아웃 알림
  handleLogout();

  clearUser();
  clearJwt();
}
